import { Heart, ShieldCheck, Shield, Lock, Server } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";

const GitHubIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    className={className}
  >
    <path d="M12 .5a12 12 0 0 0-3.79 23.4c.6.11.82-.26.82-.58v-2.02c-3.34.73-4.04-1.61-4.04-1.61-.55-1.39-1.34-1.76-1.34-1.76-1.1-.75.08-.73.08-.73 1.22.09 1.86 1.25 1.86 1.25 1.08 1.85 2.83 1.32 3.52 1.01.11-.79.42-1.32.76-1.62-2.67-.3-5.47-1.34-5.47-5.95 0-1.31.47-2.38 1.24-3.22-.12-.3-.54-1.52.12-3.17 0 0 1.01-.32 3.3 1.23a11.5 11.5 0 0 1 6 0c2.28-1.55 3.29-1.23 3.29-1.23.66 1.65.24 2.87.12 3.17.77.84 1.23 1.91 1.23 3.22 0 4.62-2.8 5.64-5.48 5.94.43.37.81 1.1.81 2.22v3.29c0 .32.21.7.82.58A12 12 0 0 0 12 .5z"/>
  </svg>
);

const LinkedInIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    className={className}
  >
    <path d="M4.98 3.5A2.5 2.5 0 1 1 0 3.5a2.5 2.5 0 0 1 4.98 0zM.5 8h4.95V23H.5V8zM8 8h4.74v2.05h.07C13.55 8.76 15.1 8 16.97 8 21.02 8 23 10.54 23 14.62V23h-4.95v-7.3c0-1.74-.03-3.97-2.42-3.97-2.43 0-2.8 1.9-2.8 3.85V23H8V8z"/>
  </svg>
);

export const Footer = () => {
  const { t } = useTranslation();
  const { lang } = useParams<{ lang?: string }>();
  const taglines = ["Get advanced analytics", "Boost engagement", "Build a thriving workplace"];
  const [taglineIndex, setTaglineIndex] = useState(0);
  const [feedbackState, setFeedbackState] = useState<'idle' | 'form' | 'loading' | 'success'>('idle');
  const [feedbackRating, setFeedbackRating] = useState<'positive' | 'negative' | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  
  useEffect(() => {
    const id = setInterval(() => setTaglineIndex((i) => (i + 1) % taglines.length), 4000);
    return () => clearInterval(id);
  }, []);

  const handleFeedbackClick = () => {
    setFeedbackState('form');
  };

  const handleFeedbackSubmit = async () => {
    if (!feedbackRating) return;
    
    setFeedbackState('loading');
    
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      
      const { error } = await supabase.functions.invoke('submit-feedback', {
        body: {
          rating: feedbackRating,
          message: feedbackMessage.trim() || undefined,
        },
      });

      if (error) {
        console.error('Error submitting feedback:', error);
        throw error;
      }

      setFeedbackState('success');
      
      // Reset to idle after 3 seconds
      setTimeout(() => {
        setFeedbackState('idle');
        setFeedbackRating(null);
        setFeedbackMessage('');
      }, 3000);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      setFeedbackState('idle');
      // You could add a toast notification here for error feedback
    }
  };

  const handleCancelFeedback = () => {
    setFeedbackState('idle');
    setFeedbackRating(null);
    setFeedbackMessage('');
  };

  return (
    <footer className="bg-muted/30 border-t mt-16">
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4 col-span-2 md:col-span-1">
            <div className="flex items-center gap-2">
              <img 
                src="/lovable-uploads/822c9934-bc7f-4f51-afef-cd4280c5d405.png" 
                alt="Jigso logo" 
                className="w-6 h-6 rounded-sm"
              />
              <h3 className="font-bold text-lg">JiGSO</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              {t('footer.empoweringOrganizations')}
            </p>
            {/* Rotating Tagline */}
            <div className="h-6">
              <span key={taglineIndex} className="text-sm text-foreground/80 animate-fade-in">
                {taglines[taglineIndex]}
              </span>
            </div>
          </div>

          {/* Important Links */}
          <nav aria-label="Footer navigation" className="space-y-4">
            <h4 className="font-semibold">{t('footer.quickLinks')}</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>
                <Link to={`/${lang || 'en'}`} className="hover:text-foreground transition-colors">
                  {t('footer.allTools')}
                </Link>
              </li>
              <li><a href="https://jigso.com/company/meet-the-team/" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">{t('footer.about')}</a></li>
              <li><a href={`/${lang || 'en'}/privacy`} target="_self" rel="noopener noreferrer" className="hover:text-foreground transition-colors">{t('footer.privacyPolicy')}</a></li>
              <li><a href="https://jigso.com/company/contact" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">{t('footer.contact')}</a></li>
            </ul>
          </nav>

          {/* Security & Compliance - Column */}
          <div className="space-y-4">
            <h4 className="font-semibold">{t('footer.securityCompliance.title')}</h4>
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant="secondary" className="flex items-center gap-2 whitespace-nowrap">
                <ShieldCheck className="w-4 h-4 pulse" aria-hidden="true" />
                <span className="text-xs">{t('footer.securityCompliance.gdprCompliant')}</span>
              </Badge>
              <Badge variant="secondary" className="flex items-center gap-2 whitespace-nowrap">
                <Shield className="w-4 h-4 pulse" aria-hidden="true" />
                <span className="text-xs">{t('footer.securityCompliance.privacyByDesign')}</span>
              </Badge>
              <Badge variant="secondary" className="flex items-center gap-2 whitespace-nowrap">
                <Server className="w-4 h-4 pulse" aria-hidden="true" />
                <span className="text-xs">{t('footer.securityCompliance.euHosted')}</span>
              </Badge>
              {/* <Badge variant="secondary" className="flex items-center gap-2 whitespace-normal md:whitespace-nowrap">
                <Lock className="w-4 h-4 pulse" aria-hidden="true" />
                <span className="text-xs">{t('footer.securityCompliance.secureDataEncryption')}</span>
              </Badge> */}
              <a
                href="https://app.aikido.dev/audit-report/external/9c1Otg7j8a5bdgpa5VH4QFMS/request"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Aikido Security Audit Report"
                className="inline-flex items-center p-2"
              >
                <img
                  src="https://app.aikido.dev/assets/badges/full-light-theme.svg"
                  alt="Aikido Security Audit Report"
                  height={40}
                  loading="lazy"
                />
              </a>
            </div>
          </div>

          {/* Feedback Widget - Column */}
          <div className="hidden md:block space-y-4 col-span-2 md:col-span-1">
            <h4 className="font-semibold inline-flex items-center gap-2">
              <img 
                  src="/lovable-uploads/JiGSO_analytics.png" 
                  alt="JiGSO Logo" 
                  className="w-4 h-4 animate-logo-rotate opacity-90 hover:opacity-100 transition-opacity duration-300"
                />
              {t('footer.feedback.title')}
              <Badge variant="secondary" className="px-1.5 py-0.5 gap-1">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary animate-pulse" aria-hidden="true" />
                <span className="text-[10px] font-semibold uppercase tracking-wide">{t('footer.beta.badge')}</span>
              </Badge>
            </h4>
            <div className="space-y-3">
              {feedbackState !== 'form' && (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    {t('footer.feedback.subtitle')}
                  </p>
                  <a 
                    href="https://jigsoexhub.featurebase.app/roadmap" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:text-primary/80 transition-colors underline"
                  >
                    {t('footer.feedback.viewRoadmap')}
                  </a>
                </div>
              )}
              <div className="flex flex-col gap-2">
                {feedbackState === 'idle' && (
                  <button 
                    onClick={handleFeedbackClick}
                    className="flex items-center justify-center gap-2 px-3 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                  >
                    <Heart className="w-4 h-4" />
                    {t('footer.feedback.sendFeedback')}
                  </button>
                )}

                {feedbackState === 'form' && (
                  <div className="space-y-3 p-3 bg-muted/50 rounded-md border">
                    <div className="flex items-center justify-center gap-4">
                      <button
                        onClick={() => setFeedbackRating('positive')}
                        className={`p-2 rounded-full transition-colors ${
                          feedbackRating === 'positive' 
                            ? 'bg-green-100 text-green-600 border-2 border-green-300' 
                            : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                        }`}
                        aria-label="Positive feedback"
                      >
                        👍
                      </button>
                      <button
                        onClick={() => setFeedbackRating('negative')}
                        className={`p-2 rounded-full transition-colors ${
                          feedbackRating === 'negative' 
                            ? 'bg-red-100 text-red-600 border-2 border-red-300' 
                            : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                        }`}
                        aria-label="Negative feedback"
                      >
                        👎
                      </button>
                    </div>
                    
                    <textarea
                      placeholder="Tell us more (optional)..."
                      value={feedbackMessage}
                      onChange={(e) => setFeedbackMessage(e.target.value)}
                      className="w-full p-2 text-xs border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary/20"
                      rows={2}
                    />
                    
                    <div className="flex gap-2">
                      <button
                        onClick={handleFeedbackSubmit}
                        disabled={!feedbackRating}
                        className="flex-1 px-3 py-2 text-xs bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Submit
                      </button>
                      <button
                        onClick={handleCancelFeedback}
                        className="px-3 py-2 text-xs bg-muted text-muted-foreground rounded-md hover:bg-muted/80 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {feedbackState === 'loading' && (
                  <button disabled className="flex items-center justify-center gap-2 px-3 py-2 text-sm bg-primary text-primary-foreground rounded-md opacity-50 cursor-not-allowed">
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    {t('footer.feedback.sending')}
                  </button>
                )}

                {feedbackState === 'success' && (
                  <button disabled className="flex items-center justify-center gap-2 px-3 py-2 text-sm bg-primary text-primary-foreground rounded-md opacity-50 cursor-not-allowed">
                    <ShieldCheck className="w-4 h-4" />
                    {t('footer.feedback.thankYou')}
                  </button>
                )}
              </div>
              {/* <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <span className="text-yellow-500">★</span>
                    <span>4.8/5</span>
                  </span>
                  <span>•</span>
                  <span>98% satisfaction</span>
              </div> */}
            </div>
          </div>
        </div>




        {/* Bottom Section */}
        <div className="flex flex-col md:flex-row items-center justify-between pt-8 mt-8 border-t">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4 md:mb-0">
            <span>{t('footer.madeWith')}</span>
            <Heart className="w-4 h-4 text-red-500 fill-current" />
            <span>{t('footer.byThe')}<a href="https://jigso.com/company/meet-the-team/" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors"> JiGSO team</a></span>
          </div>
          
          <div className="flex items-center gap-4">
            <a href="https://github.com/jigso-com" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors" aria-label="GitHub">
              <GitHubIcon className="w-5 h-5" />
            </a>
            <a href="https://linkedin.com/company/jigso" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors" aria-label="LinkedIn">
              <LinkedInIcon className="w-5 h-5" />
            </a>
          </div>
        </div>

        <div className="text-center pt-4 text-xs text-muted-foreground">
          © {new Date().getFullYear()} JiGSO. {t('footer.allRightsReserved')}
        </div>
      </div>
    </footer>
  );
};